import React, { useState, useEffect } from 'react';
import './SettingsScreen.css';
import UpgradeModal from './UpgradeModal';
import Icon from '@mdi/react';
import { mdiChevronLeft } from '@mdi/js';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import api from '../services/api';
import { Flex } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';


// Define a custom event for API key changes
const API_KEY_UPDATED_EVENT = 'openRouterApiKeyUpdated';

// Generate a random string for code_verifier
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => ('0' + (byte & 0xFF).toString(16)).slice(-2)).join('');
};

// Generate SHA-256 code challenge
const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  
  // Convert hash to base64url encoding
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray));
  
  return hashBase64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Start OAuth flow
export const initiateOpenRouterAuth = async () => {
  try {
    // Generate code verifier
    const codeVerifier = generateCodeVerifier();
    
    // Generate code challenge
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later use
    sessionStorage.setItem('openrouter_code_verifier', codeVerifier);
    sessionStorage.setItem('openrouter_code_method', 'S256');
    
    // Callback URL (current page)
    const callbackUrl = window.location.href.split('?')[0];
    
    // OpenRouter auth URL
    const authUrl = new URL('https://openrouter.ai/auth');
    authUrl.searchParams.append('callback_url', callbackUrl);
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    
    // Redirect to auth URL
    window.location.href = authUrl.toString();
    
  } catch (error) {
    console.error('Error initiating OAuth flow:', error);
    alert(`Failed to start OpenRouter connection: ${error.message}`);
  }
};

export const SettingsScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [openRouterApiKey, setOpenRouterApiKey] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  useEffect(() => {
    // Load user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Check for API key in localStorage
    const savedApiKey = localStorage.getItem('openRouterApiKey');
    if (savedApiKey) {
      setOpenRouterApiKey(savedApiKey);
    }
    
    // Handle callback from OAuth process
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleCodeExchange(code);
    }
  }, []);

  const handleOpenUpgradeModal = () => {
    setIsUpgradeModalOpen(true);
  };
  
  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
  };
  
  const handleSubscribe = (plan) => {
    console.log(`User selected the ${plan} plan`);
    // Here you would typically integrate with your payment processor
    // For now, we'll just update the user object
    
    const updatedUser = {
      ...user,
      subscription_active: true,
      subscription_plan: plan,
      subscription_start_date: Date.now(),
      // Set subscription end date to 1 month or 1 year from now depending on plan
      subscription_end_date: plan === 'monthly' 
        ? Date.now() + (30 * 24 * 60 * 60 * 1000) 
        : Date.now() + (365 * 24 * 60 * 60 * 1000)
    };
    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };
  
  const handleOpenCancelModal = () => {
    setIsCancelModalOpen(true);
  };
  
  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
  };
  
  const handleCancelSubscription = async () => {
    await api.cancelSubscription(user.email);
    setIsCancelModalOpen(false);
  };
  
  // Exchange code for API key
  const handleCodeExchange = async (code) => {
    try {
      setIsConnecting(true);
      
      // Retrieve code_verifier from sessionStorage
      const codeVerifier = sessionStorage.getItem('openrouter_code_verifier');
      const codeMethod = sessionStorage.getItem('openrouter_code_method');
      
      if (!codeVerifier) {
        throw new Error('Code verifier not found. Please try connecting again.');
      }

      const response = await fetch('https://openrouter.ai/api/v1/auth/keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,
          code_verifier: codeVerifier,
          code_challenge_method: codeMethod || 'S256',
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get API key: ${response.status} ${errorText}`);
      }
      
      const { key } = await response.json();
      console.log('Received OpenRouter API key:', key);
      
      // Store the API key
      setOpenRouterApiKey(key);
      localStorage.setItem('openRouterApiKey', key);
      
      // Notify the application that the API key has been updated
      window.dispatchEvent(new CustomEvent(API_KEY_UPDATED_EVENT, { detail: { key } }));
      
      // Clean up
      sessionStorage.removeItem('openrouter_code_verifier');
      sessionStorage.removeItem('openrouter_code_method');
      
      // Remove code from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, document.title, url.toString());
      
    } catch (error) {
      console.error('Error exchanging code for API key:', error);
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Clear API key
  const disconnectOpenRouter = () => {
    localStorage.removeItem('openRouterApiKey');
    setOpenRouterApiKey('');
    
    // Notify the application that the API key has been removed
    window.dispatchEvent(new CustomEvent(API_KEY_UPDATED_EVENT, { detail: { key: null } }));
  };

  // Format a date in a user-friendly format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="settings-container">
      <Flex onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} >
                <Icon path={mdiChevronLeft} size={1} />
                <div>Back</div>
            </Flex>

      <h2>Account Settings</h2>
      
      {user && (
        <div className="settings-sections">
          <div className="settings-section">
            <h3>Account Settings</h3>
            <div className="settings-field">
              <label>Name</label>
              <div>{user.name}</div>
            </div>
            <div className="settings-field">
              <label>Email</label>
              <div>{user.email}</div>
            </div>
          </div>
          
          {/* Subscription Section */}
          <div className="settings-section">
            <h3>Subscription</h3>
            {user.subscription_active ? (
              <div>
                <div className="settings-field">
                  <label>Status</label>
                  <div className="subscription-status active">Active</div>
                </div>
                  <div className="subscription-actions">
                    {user.cancellation_date ? (
                      <div className="cancellation-notice">
                        Subscription will be cancelled on {new Date(user.cancellation_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </div>
                    ) : (
                      <button 
                        className="cancel-subscription-button" 
                        onClick={handleOpenCancelModal}
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
              </div>
            ) : (
              <div>
                <div className="settings-field">
                  <label>Status</label>
                  {user.trial_end_date && Date.now() < parseInt(user.trial_end_date, 10) ? (
                    <div className="subscription-status trial">
                      Trial (expires {formatDate(parseInt(user.trial_end_date, 10))})
                    </div>
                  ) : (
                    <div className="subscription-status inactive">No active subscription</div>
                  )}
                </div>
                <div className="subscription-actions">
                  <button 
                    className="upgrade-now-button"
                    onClick={handleOpenUpgradeModal}
                  >
                    Upgrade to Premium
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="settings-section">
            <h3>API Integrations</h3>
            <div className="settings-field">
              <label>OpenRouter</label>
              <div className="api-connection">
                {openRouterApiKey ? (
                  <div className="api-status connected">
                    <span className="status-indicator"></span>
                    Connected
                  </div>
                ) : (
                  <div className="api-status not-connected">
                    <span className="status-indicator"></span>
                    Not Connected
                  </div>
                )}
                {isConnecting ? (
                  <button className="oauth-button loading" disabled>
                    <span className="loading-spinner"></span>
                    Connecting...
                  </button>
                ) : openRouterApiKey ? (
                  <div className="button-group">
                    <button 
                      className="oauth-button secondary"
                      onClick={initiateOpenRouterAuth}
                    >
                      Reconnect
                    </button>
                    <button 
                      className="oauth-button disconnect"
                      onClick={disconnectOpenRouter}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button 
                    className="oauth-button"
                    onClick={initiateOpenRouterAuth}
                  >
                    Connect with OpenRouter
                  </button>
                )}
              </div>
              {openRouterApiKey && (
                <div className="api-key-info">
                  <p className="api-key-help">
                    Your OpenRouter API key is securely stored in your browser.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {!user && <p>Please log in to view settings.</p>}
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={handleCloseUpgradeModal} 
      />
      
      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={handleCancelSubscription}
      />
    </div>
  );
};

export default SettingsScreen;

// Export the event name for other components to listen to
export { API_KEY_UPDATED_EVENT };
